import React, { useContext, useMemo } from 'react';
import classNames from 'classnames';
import Link from 'next/link';
import { useRouter } from 'next/router';
import {
  Backpack,
  Keychain,
  Locale,
  Metaframe,
  Profile,
} from '@xsolla/metaframe-react-sdk';
import { FormattedMessage } from 'react-intl';
import RewardsLogo from '@/assets/xsolla_rewards_logo.svg';
import styles from './Header.module.css';
import { ROUTE } from '@/lib/constants';
import { useInventoryStore } from '@/stores';
import TabPanel, { TabsConfig } from '@/components/ui/TabPanel';
import { AuthContext } from '@/providers/AuthProvider';
import { useIsAuthorized } from '@/hooks/useIsAuthorized';

export default function TopPanel(): React.ReactElement {
  const authContext = useContext(AuthContext);

  const isAuthorized = useIsAuthorized();

  const cleanInventoryState = useInventoryStore((state) => state.cleanInventoryState);

  const router = useRouter();

  const loginProjectId = useMemo(() => process.env.NEXT_PUBLIC_LOGIN_WIDGET_PROJECT_ID || '', []);
  // TODO: Implement ConfigCat for this
  const isKeychainEnabled = useMemo(
    () => (process.env.NEXT_PUBLIC_KEYCHAIN_ENABLED && process.env.NEXT_PUBLIC_KEYCHAIN_ENABLED === 'true') || false,
    [],
  );
  const isBackpackEnabled = useMemo(
    () => (process.env.NEXT_PUBLIC_BACKPACK_ENABLED && process.env.NEXT_PUBLIC_BACKPACK_ENABLED === 'true') || false,
    [],
  );

  const tabsConfig: TabsConfig = [
    {
      path: ROUTE.INDEX,
      title: <FormattedMessage id="navigation_panel.gallery_tab" />,
    },
    {
      path: ROUTE.INVENTORY_ROUTE,
      title: <FormattedMessage id="navigation_panel.inventory_tab" />,
    },
  ];

  const onClickLogout = () => {
    authContext.removeToken();

    if (router.pathname.startsWith(ROUTE.INVENTORY_ROUTE)) {
      return router.push(ROUTE.INDEX)
        .then(() => cleanInventoryState());
    }

    return cleanInventoryState();
  };

  const loginHandler = (token: string) => {
    authContext.setToken(token);
  };

  return (
    <div className={classNames(styles.header)}>
      <Link href={ROUTE.INDEX} className={classNames(styles['header__drops-logo'])}>
        <RewardsLogo />
      </Link>
      <div className={classNames(
        styles['header__tabs-container'],
        { [styles['header__tabs-container--hidden']]: !isAuthorized },
      )}
      >
        {isAuthorized && <TabPanel tabsConfig={tabsConfig} />}
      </div>
      <div className="overlay-wrapper">
        <Metaframe
          onLogout={onClickLogout}
          onLogin={loginHandler}
          locale={Locale.en_US}
          loginProjectId={loginProjectId}
        >
          {isBackpackEnabled && <Backpack />}
          {isKeychainEnabled && <Keychain />}
          <Profile />
        </Metaframe>
      </div>
    </div>
  );
}
